import { max } from 'd3-array';
import { useState } from 'react';
import { scaleLinear } from 'd3-scale';
import type { Distribution } from '@upbeat/shared';
import { barStyle, chartColors, chartFont } from '../../../design/charts.js';
import { fmtInt, fmtValue, tickSpecifier } from '../lib/format';
import { Tooltip, useTooltip } from './Tooltip';
import { useMeasure } from './useMeasure';
import { t } from '../i18n';

interface Props { data: Distribution; height?: number }

const M = { top: 12, right: 12, bottom: 40, left: 44 };
const GAP = 2; // surface gap between adjacent bins

/** Column histogram over the API's [p01, p99] bins. Counts on the y axis, values on x. */
export function Histogram({ data, height = 300 }: Props) {
  const { ref, width } = useMeasure();
  const { tip, show, hide } = useTooltip();
  const [hover, setHover] = useState<number | null>(null);
  const innerW = Math.max(80, width - M.left - M.right);
  const innerH = height - M.top - M.bottom;
  const { bins, units } = data;

  const xDomain: [number, number] = bins.length ? [bins[0]!.x0, bins[bins.length - 1]!.x1] : [0, 1];
  const x = scaleLinear().domain(xDomain).range([0, innerW]);
  const y = scaleLinear().domain([0, max(bins, (b) => b.count) ?? 1]).nice().range([innerH, 0]);
  const yTicks = y.ticks(4).filter(Number.isInteger);
  const xTicks = x.ticks(width < 500 ? 4 : 6);
  const xf = x.tickFormat(6, tickSpecifier(units));

  return (
    <div className="chart" ref={ref}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label={t.distAria}>
          <g transform={`translate(${M.left},${M.top})`}>
            {yTicks.map((t) => (
              <g key={t} transform={`translate(0,${y(t)})`}>
                <line x1={0} x2={innerW} stroke={chartColors.grid} />
                <text x={-10} y={4} textAnchor="end" fill={chartColors.axisText} fontSize={chartFont.tick.size} fontFamily={chartFont.tick.family}>{t}</text>
              </g>
            ))}
            {bins.map((b, i) => {
              const bx = x(b.x0) + GAP / 2;
              const bw = Math.max(1, x(b.x1) - x(b.x0) - GAP);
              const top = y(b.count);
              const h = innerH - top;
              const rr = Math.min(barStyle.radius / 2, bw / 2, h);
              const hot = hover === i;
              return (
                <g key={i}
                   onPointerMove={(e) => { show(e, <BinTip b={b} units={units} />); setHover(i); }}
                   onPointerLeave={() => { hide(); setHover(null); }}>
                  <rect x={x(b.x0)} y={0} width={Math.max(1, x(b.x1) - x(b.x0))} height={innerH} fill="transparent" />
                  {h > 0 && (
                    <path
                      d={`M${bx},${innerH} v${-(h - rr)} a${rr},${rr} 0 0 1 ${rr},${-rr} h${bw - 2 * rr} a${rr},${rr} 0 0 1 ${rr},${rr} v${h - rr} z`}
                      fill={hot ? chartColors.primaryText : chartColors.primary}
                    />
                  )}
                </g>
              );
            })}
            <line x1={0} x2={innerW} y1={innerH} y2={innerH} stroke={chartColors.axisText} />
            {xTicks.map((t) => (
              <text key={t} x={x(t)} y={innerH + 22} textAnchor="middle" fill={chartColors.axisText} fontSize={chartFont.tick.size} fontFamily={chartFont.tick.family}>{xf(t)}</text>
            ))}
          </g>
        </svg>
      )}
      <Tooltip tip={tip} width={width} />
    </div>
  );
}

function BinTip({ b, units }: { b: Distribution['bins'][number]; units: string | null }) {
  return (
    <div>
      <div className="tip-v">{fmtInt(b.count)} <span className="tip-l">{t.utilitiesWord}</span></div>
      <div className="tip-s">{t.binRange(fmtValue(units, b.x0), fmtValue(units, b.x1))}</div>
    </div>
  );
}
